/**
 * Tipos y llamada REAL de `GET /consola/viajes` (`apps/api/src/viajes/consola-viajes.controller.
 * ts`). Mismo criterio que el resto de los clientes reales de `dominio/`.
 *
 * 🔑 **Acá los enums SÍ coinciden con el mock, y está medido, no supuesto.** `EstadoViaje`,
 * `CanceladoPor` y `TipoVehiculo` de `@taxi/contratos` (que es lo que reexporta `dominio/
 * contratos.ts`) tienen exactamente los mismos valores que los enums de `apps/api/prisma/schema.
 * prisma`, cotejados uno a uno. Por eso —y sólo por eso— `Viajes.tsx` puede seguir usando
 * `componentes/Etiqueta.tsx` (`tipo="viaje"`) y `TIPO_VEHICULO`, a diferencia de `Conductores.tsx`
 * / `Suscripciones.tsx`, donde tres de cuatro diccionarios NO coinciden y hubo que escribir
 * etiquetas propias. **La regla no es «reusar el mock» ni «nunca reusarlo»: es cotejar valor por
 * valor y decir cuál de los dos casos es.**
 *
 * 🔴 **LO QUE ESTE ENDPOINT NO TRAE, y por eso la tabla perdió columnas que el mock tenía.** El
 * servicio lo dice en su propio encabezado: es «lo suficiente para operar, NO el detalle
 * completo». Fuera del listado quedan **`origen` y `destino`** (con su texto), **`etaRecogidaSeg`,
 * `distanciaRutaM` y `estimadoDegradado`**. Ninguno de los cinco existe en `ViajeListado`, y no
 * hay hoy un `GET /consola/viajes/:id` de detalle donde buscarlos ⇒ esas columnas se retiraron en
 * vez de rellenarse con algo. Un «—» permanente en cinco columnas se lee como «este viaje no tuvo
 * ruta», que es falso.
 *
 * 🔴 **NO EXISTE ningún endpoint de OFERTAS de un viaje.** Barrido de los 40 `@Get`/`@Post` de
 * `apps/api/src`: `dispatch.controller.ts` expone `aceptar`, `buscar`, `vencimientos` y
 * `tasa-victoria`, ninguno lista las ofertas de un `viajeId`. Por eso `Viajes.tsx` retiró el
 * panel «Ofertas de …» en vez de dejarlo con `OFERTAS_EN_VUELO`: al lado de filas REALES, una
 * tabla de ofertas inventadas es peor que ninguna — quien la lea no tiene cómo saber que esa
 * mitad de la pantalla no ocurrió.
 *
 * ⚠️ **Grado de verificación, declarado (§2):** contrato leído del servicio + del DTO reales y
 * cotejado contra `consola-viajes.integration.spec.ts`. **No** hubo un `fetch` desde el bundle de
 * la consola contra un backend vivo (mismo límite que declara `clienteApi.ts`).
 */
import { hayBackend } from '../config/entorno';
import { obtener } from './clienteApi';
import type { CanceladoPor, EstadoViaje, TipoVehiculo } from './contratos';

/**
 * ⚠️ **Los tres campos de la tarifa son STRING, no number** — `Prisma.Decimal` serializa a texto
 * sobre HTTP (`"12.50"`, no `12.5`), la misma trampa que ya documentan `verificacionApi.
 * ConductorDetalle.reputacion` e `incidentesApi.IncidenteReal.lat`. El servicio los declara
 * `unknown` para no mentir sobre el tipo; acá se declara lo que viaja por el cable.
 *
 * 🔎 **Y hay un caso donde llegan como NUMBER: el doble de pruebas del propio backend**
 * (`consola-viajes.prisma-doble.testing.ts` guarda `number` de JS, y su spec lo dice explícito).
 * Contra Postgres real son strings. Por eso el formateo de la pantalla pasa por `Number()`, que
 * tolera las dos formas — no por confiar en una.
 */
export interface TarifaReferencialListado {
  min: string;
  max: string;
  /** Código ISO-4217 (`PEN`). */
  moneda: string;
}

/** Espejo exacto de `ViajeListado` (`consola-viajes.service.ts`). */
export interface ViajeListado {
  id: string;
  estado: EstadoViaje;
  /** `null` = viaje ANONIMIZADO por la retención (90 días, issue #176): ya no tiene identidad. */
  pasajeroId: string | null;
  /** `null` si el pasajero no tiene nombre cargado o su cuenta se despersonalizó. */
  pasajeroNombre: string | null;
  /** `null` mientras el viaje no tenga conductor asignado (SOLICITADO/BUSCANDO/SIN_CONDUCTOR). */
  conductorId: string | null;
  conductorNombre: string | null;
  claseSolicitada: TipoVehiculo;
  /** `null` si el viaje nunca resolvió tarifa referencial. Nunca un objeto a medias. */
  tarifaReferencial: TarifaReferencialListado | null;
  solicitadoAt: string;
  asignadoAt: string | null;
  finAt: string | null;
  canceladoAt: string | null;
  /** Presente sólo si `estado === 'CANCELADO'`. El QUIÉN va acá, no en el estado. */
  canceladoPor: CanceladoPor | null;
  motivoCancelacion: string | null;
}

/**
 * 🔴 **TOPE DEL BACKEND, ESPEJADO A PROPÓSITO** — misma constante y mismo motivo que
 * `TOPE_FILAS_CONDUCTORES`: `ConsolaViajesService` corta con `take: 200` y la respuesta no trae
 * ninguna marca de que cortó. La cantidad de filas es la única señal que tiene la consola.
 */
export const TOPE_FILAS_VIAJES = 200;

export interface FiltroViajes {
  estado?: EstadoViaje;
  /** ISO-8601. El backend lo valida con `@IsISO8601` y rechaza con 400 si no lo es. */
  desde?: string;
  /** ISO-8601, **EXCLUSIVO** (`lt`), verificado en el spec del backend. */
  hasta?: string;
}

/**
 * `estado` se manda AL BACKEND, no se filtra client-side — mismo motivo que en
 * `obtenerConductores`: con `take: 200` ordenado por `solicitadoAt desc`, filtrar en el cliente
 * buscaría dentro de los 200 viajes más recientes y un estado poco frecuente daría vacío
 * teniendo filas de sobra en la base.
 *
 * ⚠️ El backend NO tiene búsqueda de texto: buscar por código/pasajero/conductor sigue siendo
 * client-side sobre lo que llegó, con el límite que el aviso de tope explica en pantalla.
 */
export const obtenerViajes = (filtro: FiltroViajes = {}): Promise<ViajeListado[]> => {
  if (!hayBackend) {
    return Promise.reject(
      new Error(
        'No hay backend configurado (falta VITE_API_URL): esta pantalla no tiene a quién pedirle los viajes.',
      ),
    );
  }
  const parametros = new URLSearchParams();
  if (filtro.estado) parametros.set('estado', filtro.estado);
  if (filtro.desde) parametros.set('desde', filtro.desde);
  if (filtro.hasta) parametros.set('hasta', filtro.hasta);
  const cadena = parametros.toString();
  return obtener<ViajeListado[]>(`/consola/viajes${cadena ? `?${cadena}` : ''}`);
};
